import { Box, Button, ButtonGroup, Flex, Icon, Image, Tooltip, useToast } from '@homebotapp/hb-react-component-catalog'
import { defineMessages, useIntl } from 'react-intl'
import { useSelector } from 'react-redux'
import { useSendListingMessage } from '../../../../../../hooks/listings/useSendListingMessage'
import { useEffect, useState } from 'react'
import {
  selectCustomerFirstName,
  selectCustomerHasSponsor,
  selectCustomerIsLoanOfficer,
  selectCustomerIsRealEstateAgent,
  selectCustomerPhotoUri
} from '../../../../../../store/selectors/customerProfile'
import { MessageInput, MessageTopicKey } from '../../../../../../api/gqlaxy/gql/generated/graphql'
import { selectSponsorshipFirstName, selectSponsorshipPhotoUri } from '../../../../../../store/selectors/sponsorship'
import * as Sentry from '@sentry/browser'
import { ConfettiTarget } from '../../../../../shared/Confetti'
import { hasCustomerToken } from '../../../../../../auth'
import {
  VerificationFlowProvider,
  VerificationFlowManager,
  useVerificationFlow
} from '../../../../../shared/VerificationFlow'
import { selectPreferredLoanOfficerMembership } from '../../../../../../store/selectors/clientTeamMemberships'
import { useVerificationFlowEnabled } from '../../../../../../hooks/useVerificationFlowEnabled'
import { CUSTOMER_MESSAGES } from '../../../../../../constants/customerMessages'
import { useCustomerBranding } from '../../../../../../hooks/homebotNetwork/usePreferredAgentOrCustomer'

const MSG = defineMessages({
  cancel: {
    id: 'BuyDownDirectMessage.cancel',
    defaultMessage: 'Cancel'
  },
  confirm: {
    id: 'BuyDownDirectMessage.confirm',
    defaultMessage: 'Confirm'
  },
  buyDownDm: {
    id: 'BuyDownDirectMessage.buyDownDm',
    defaultMessage: 'Ask {name} about buydowns'
  },
  buyDownDmGeneral: {
    id: 'BuyDownDirectMessage.buyDownDmGeneral',
    defaultMessage: 'Ask {name} about these listings'
  },
  success: {
    id: 'BuyDownDirectMessage.success',
    defaultMessage: 'Direct message sent!'
  },
  fail: {
    id: 'BuyDownDirectMessage.fail',
    defaultMessage: 'Direct message failed to send.'
  },
  impersonation: {
    id: 'BuyDownDirectMessage.impersonation',
    defaultMessage: 'Messaging not available when impersonating client'
  }
})

/**
 * DM animation is based off EstimatedMonthlyPayment
 *
 * Goal is to eventually move this whole thing to the DS
 */
enum DMStatus {
  Initial = 'initial',
  Confirmation = 'confirmation',
  Success = 'success',
  Fail = 'fail'
}

// this let's the confetti animation finish before resetting the state
const debounce = (func, delay) => {
  let timer
  return function (...args) {
    clearTimeout(timer)
    timer = setTimeout(() => {
      func.apply(this, args)
    }, delay)
  }
}

const animationTimer = 200

interface BuyDownDirectMessageProps {
  listingId?: string
  zipcode?: string
  galleryType?: string
}

// Internal component that implements the direct message UI (without verification flow)
const BuyDownDirectMessageInternal = ({ listingId }: BuyDownDirectMessageProps) => {
  const intl = useIntl()
  const { mutateAsync: sendListingMessage } = useSendListingMessage()

  const [isSending, setIsSending] = useState(false)
  const [dmStatus, setDmStatus] = useState<DMStatus>(DMStatus.Initial)
  const [showMiniConfetti, setShowMiniConfetti] = useState(false)
  const [showMiniConfettiAlt, setShowMiniConfettiAlt] = useState(false)
  const [confettiCounter, setConfettiCounter] = useState(0)

  const isCustomerLO = useSelector(selectCustomerIsLoanOfficer)
  const isCustomerREA = useSelector(selectCustomerIsRealEstateAgent)
  const isCustomerSponsored = useSelector(selectCustomerHasSponsor)
  const isImpersonating = hasCustomerToken()
  const customerPhoto = useSelector(selectCustomerPhotoUri)
  const customerFirstName = useSelector(selectCustomerFirstName)

  const sponsorPhoto = useSelector(selectSponsorshipPhotoUri)
  const sponsorFirstName = useSelector(selectSponsorshipFirstName)

  const handleFinalStatus = () => {
    if (dmStatus === DMStatus.Fail) {
      setDmStatus(DMStatus.Initial)
    }

    if (dmStatus === DMStatus.Success) {
      setConfettiCounter(prevCounter => prevCounter + 1)
      const actionIndex = confettiCounter % 4

      switch (actionIndex) {
        case 0:
          setShowMiniConfetti(true)
          setShowMiniConfettiAlt(false)
          break
        case 1:
          setShowMiniConfettiAlt(true)
          break
        case 2:
          setShowMiniConfetti(true)
          break
        case 3:
          setShowMiniConfettiAlt(true)
          setTimeout(() => {
            if (confettiCounter % 4 === 3) {
              setShowMiniConfetti(false)
              setShowMiniConfettiAlt(false)
            }
          }, animationTimer)
          break
      }
    }
  }

  const debouncedHandleFinalStatus = debounce(handleFinalStatus, animationTimer)

  const handleInitialButtonClick = () => {
    setDmStatus(DMStatus.Confirmation)
  }

  const handleSubmit = async () => {
    setIsSending(true)

    const input: MessageInput = {
      topicKey: MessageTopicKey.ListingsDirectMessageToSponsor,
      message: isCustomerREA
        ? 'Can you tell me more about this listing?'
        : 'Tell me more about Buy Downs, is this a good option for me?',
      topic: 'buy downs as a seller concession'
    }

    if (listingId) {
      input.listingId = listingId
    }

    try {
      await sendListingMessage({
        input
      })
      setDmStatus(DMStatus.Success)
    } catch (error: any) {
      setDmStatus(DMStatus.Fail)

      Sentry.captureMessage('DM failed to send', {
        extra: {
          error: error.message || '',
          requestData: error.config?.data || {},
          requestErrors: Object.values(error.response?.data?.errors) || []
        }
      })
    } finally {
      setTimeout(() => {
        setIsSending(false)
      }, 300)
    }
  }

  return (
    <Flex direction='column' align='center' w='100%'>
      {/* @ts-ignore */}
      <ConfettiTarget go={dmStatus === DMStatus.Success} numberOfPieces={40}>
        {/* @ts-ignore */}
        <ConfettiTarget go={showMiniConfetti} numberOfPieces={32}>
          {/* @ts-ignore */}
          <ConfettiTarget go={showMiniConfettiAlt} numberOfPieces={72}>
            <Box position={'relative'} mb={0} overflow={'hidden'} w={'100%'}>
              <Flex direction={'column'} gap={4} transition={'.15s ease-in-out transform'}>
                <Flex direction='column' gap={4}>
                  {dmStatus === DMStatus.Initial && (
                    <>
                      {isImpersonating ? (
                        <Tooltip
                          label={intl.formatMessage(MSG.impersonation)}
                          shouldWrapChildren
                          aria-label={intl.formatMessage(MSG.impersonation)}
                          textAlign={'center'}
                        >
                          <Button
                            py='8px'
                            colorScheme='secondary'
                            width='100%'
                            isDisabled={isImpersonating}
                            variant={'outline'}
                            bg='neutral.50'
                            leftIcon={
                              <Image
                                src={isCustomerSponsored ? sponsorPhoto : customerPhoto}
                                h={6}
                                w={6}
                                borderRadius='50%'
                              />
                            }
                          >
                            {isCustomerLO || isCustomerSponsored
                              ? intl.formatMessage(MSG.buyDownDm, {
                                  name: isCustomerSponsored ? sponsorFirstName : customerFirstName
                                })
                              : intl.formatMessage(MSG.buyDownDmGeneral, { name: customerFirstName })}
                          </Button>
                        </Tooltip>
                      ) : (
                        <Button
                          py='8px'
                          colorScheme='secondary'
                          width='100%'
                          isDisabled={isImpersonating}
                          isLoading={isSending}
                          variant={'outline'}
                          bg='neutral.50'
                          onClick={handleInitialButtonClick}
                          tracking={{
                            guid: 'fvem4HRNY1anJsVBGqVXjE',
                            descriptor: 'Initial Button Press to send message'
                          }}
                          leftIcon={
                            <Image
                              src={isCustomerSponsored ? sponsorPhoto : customerPhoto}
                              h={6}
                              w={6}
                              borderRadius='50%'
                            />
                          }
                        >
                          {isCustomerLO || isCustomerSponsored
                            ? intl.formatMessage(MSG.buyDownDm, {
                                name: isCustomerSponsored ? sponsorFirstName : customerFirstName
                              })
                            : intl.formatMessage(MSG.buyDownDmGeneral, { name: customerFirstName })}
                        </Button>
                      )}
                    </>
                  )}
                  {dmStatus === DMStatus.Confirmation && (
                    <ButtonGroup width={'100%'}>
                      {!isSending && (
                        <Button
                          variant={'outline'}
                          colorScheme='secondary'
                          bg='neutral.50'
                          w='auto'
                          transition={'.2s ease-in-out all'}
                          px={12}
                          tracking={{
                            guid: '83wrMBz9TrzyGNxXrsn3NV',
                            ui_context: 'Buy Down Direct Message',
                            descriptor: 'Cancel submission of dm'
                          }}
                          onClick={() => setDmStatus(DMStatus.Initial)}
                        >
                          {intl.formatMessage(MSG.cancel)}
                        </Button>
                      )}
                      <Button
                        w='100%'
                        onClick={() => handleSubmit()}
                        colorScheme='primary'
                        isLoading={isSending}
                        tracking={{
                          guid: '8BoCxCEjj1AasmuS9AbH5Z',
                          ui_context: 'Buy Down Direct Message',
                          descriptor: 'Confirm direct message submission'
                        }}
                      >
                        {intl.formatMessage(MSG.confirm)}
                      </Button>
                    </ButtonGroup>
                  )}
                </Flex>
                {(dmStatus === DMStatus.Success || dmStatus === DMStatus.Fail) && (
                  <Button
                    w='100%'
                    variant='outline'
                    colorScheme='secondary'
                    bg='neutral.50'
                    mt={-4}
                    tracking={{
                      guid: 'tDKXTqRR4Sp8W7ZKSJG7hR',
                      ui_context: 'Buy Down Direct Message',
                      descriptor: 'Clicked on failure / success button'
                    }}
                    leftIcon={
                      dmStatus === DMStatus.Success ? <Icon name='check-circle-fa' width={3} height={3} /> : undefined
                    }
                    onClick={() => debouncedHandleFinalStatus()}
                  >
                    {dmStatus === DMStatus.Success && intl.formatMessage(MSG.success)}
                    {dmStatus === DMStatus.Fail && intl.formatMessage(MSG.fail)}
                  </Button>
                )}
              </Flex>
            </Box>
          </ConfettiTarget>
        </ConfettiTarget>
      </ConfettiTarget>
    </Flex>
  )
}

// Component that uses verification flow
const BuyDownDirectMessageWithVerification = ({ listingId, zipcode, galleryType }: BuyDownDirectMessageProps) => {
  const intl = useIntl()
  const { state, startVerification, dispatch } = useVerificationFlow()
  const { mutateAsync: sendListingMessage } = useSendListingMessage()
  const [hasTriggeredSend, setHasTriggeredSend] = useState(false)

  const isCustomerREA = useSelector(selectCustomerIsRealEstateAgent)
  const isCustomerLO = useSelector(selectCustomerIsLoanOfficer)
  const isImpersonating = hasCustomerToken()

  // Get branding - will show preferred loan officer if verified, otherwise customer/sponsor
  const branding = useCustomerBranding('LoanOfficer')
  const displayPhoto = branding?.photoUri
  const displayName = branding?.firstName

  // Watch for when verification completes and send the listing message
  useEffect(() => {
    const sendMessage = async () => {
      if (state.currentStep === 'messaging' && state.message && !hasTriggeredSend) {
        setHasTriggeredSend(true)

        const input: MessageInput = {
          topicKey: state.topicKey as MessageTopicKey,
          message: state.message,
          topic: 'buy downs as a seller concession'
        }

        // Include metadata from verification flow (listingId, zipcode, galleryType)
        if (state.metadata?.listingId) {
          input.listingId = state.metadata.listingId
        }
        if (state.metadata?.zipcode) {
          input.metadata = { ...input.metadata, zipcode: state.metadata.zipcode }
        }
        if (state.metadata?.galleryType) {
          input.metadata = { ...input.metadata, galleryType: state.metadata.galleryType }
        }

        try {
          await sendListingMessage({ input })
          dispatch({ type: 'MESSAGE_SUCCESS' })
        } catch (error: any) {
          dispatch({ type: 'MESSAGE_FAILURE' })
          Sentry.captureMessage('Buydown DM failed to send', {
            extra: {
              error: error.message || '',
              requestData: error.config?.data || {},
              requestErrors: error.response?.data?.errors ? Object.values(error.response.data.errors) : []
            }
          })
        }
      }
    }

    sendMessage()
  }, [state.currentStep, state.message, state.topicKey, state.metadata, hasTriggeredSend, sendListingMessage, dispatch])

  // Reset hasTriggeredSend when moving back to idle state
  useEffect(() => {
    if (state.currentStep === 'idle') {
      setHasTriggeredSend(false)
    }
  }, [state.currentStep])

  const handleClick = () => {
    const message = isCustomerREA
      ? 'Can you tell me more about this listing?'
      : 'Tell me more about Buy Downs, is this a good option for me?'

    const metadata = {
      ...(listingId && { listingId }),
      ...(zipcode && { zipcode }),
      ...(galleryType && { galleryType })
    }

    startVerification(
      message,
      CUSTOMER_MESSAGES.HBN_FOR_YOU_GALLERY_LOAN_OFFICER.topicKey, // verified topic key: for_you_gallery_to_preferred_loan_officer
      CUSTOMER_MESSAGES.LISTINGS_DIRECT_MESSAGE_LO.topicKey, // default topic key: listings_direct_message
      'LoanOfficer',
      metadata
    )
  }

  return (
    <Flex direction='column' align='center' w='100%'>
      {isImpersonating ? (
        <Tooltip
          label={intl.formatMessage(MSG.impersonation)}
          shouldWrapChildren
          aria-label={intl.formatMessage(MSG.impersonation)}
          textAlign={'center'}
        >
          <Button
            py='8px'
            colorScheme='secondary'
            width='100%'
            isDisabled={isImpersonating}
            variant={'outline'}
            bg='neutral.50'
            leftIcon={<Image src={displayPhoto} h={6} w={6} borderRadius='50%' />}
          >
            {intl.formatMessage(MSG.buyDownDm, { name: displayName })}
          </Button>
        </Tooltip>
      ) : (
        <Button
          py='8px'
          colorScheme='secondary'
          width='100%'
          isDisabled={isImpersonating}
          variant={'outline'}
          bg='neutral.50'
          onClick={handleClick}
          tracking={{
            guid: 'fvem4HRNY1anJsVBGqVXjE',
            descriptor: 'Initial Button Press to send message with verification'
          }}
          leftIcon={<Image src={displayPhoto} h={6} w={6} borderRadius='50%' />}
        >
          {intl.formatMessage(MSG.buyDownDm, { name: displayName })}
        </Button>
      )}
    </Flex>
  )
}

// Wrapper component that provides verification flow context
export const BuyDownDirectMessage = ({ listingId, zipcode, galleryType }: BuyDownDirectMessageProps) => {
  const loanOfficerMembership = useSelector(selectPreferredLoanOfficerMembership)
  const isVerificationFlowEnabled = useVerificationFlowEnabled()

  if (!isVerificationFlowEnabled) {
    // If verification flow is not enabled, render the internal component directly
    return <BuyDownDirectMessageInternal listingId={listingId} />
  }

  return (
    <VerificationFlowProvider
      options={{
        enableVerificationFlow: true,
        initialPartnerType: 'LoanOfficer',
        loanOfficerMembership: loanOfficerMembership,
        includePrequal: false
      }}
    >
      <VerificationFlowManager />
      <BuyDownDirectMessageWithVerification listingId={listingId} zipcode={zipcode} galleryType={galleryType} />
    </VerificationFlowProvider>
  )
}
